import React from 'react';
import { Archive, CheckCircle, XCircle, AlertCircle, Lock, Users, Receipt, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { BOUTON_PIED_CLASSES, MODAL_CONTENT_CLASSES, formatMontant, MONTH_OPTIONS } from '../nosDepensesUtils';

/**
 * Clôture mensuelle : archive définitive du mois dans `cloture_mensuelle`.
 *
 * Monté sur les primitives `Dialog` : le voile maison de `MODAL_OVERLAY_Z40_CLASS`
 * était rendu hors portail, donc neutralisé dès qu'un autre `Dialog` s'ouvrait.
 *
 * Les données viennent des endpoints dédiés (vérifier + récapitulatif), scopés
 * sur le mois choisi, et non des stats du filtre de période courant.
 */
const ClotureMoisModal = ({
  isOpen, onClose, mois, annee,
  verification, recapitulatif, isChecking,
  noteCloture, setNoteCloture,
  isLoading, onSubmit
}) => {
  const moisLabel = MONTH_OPTIONS.find((m) => m.value === Number(mois))?.label || mois;
  const peutCloturer = Boolean(verification?.peut_cloturer);
  const stats = verification?.statistiques || {};
  const totaux = recapitulatif?.totaux || {};
  const detailsGestionnaires = recapitulatif?.details_gestionnaires || [];
  const dejaCloture = verification?.raison === 'deja_cloture';

  const sansAttente = (stats.caisses_en_attente || 0) === 0;
  const sansObservation = (stats.caisses_avec_observation || 0) === 0;

  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={(ouvert) => !ouvert && !isLoading && onClose?.()}>
      <DialogContent className={`${MODAL_CONTENT_CLASSES} max-w-4xl`}>
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#c66d59] text-white">
            <Archive size={18} />
          </div>
          <div className="min-w-0 flex-1 text-left">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Clôturer {moisLabel} {annee}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              Archive comptable définitive du mois : totaux, gestionnaires, caisses et transactions.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-4">
          {isChecking ? (
            <div className="flex h-40 items-center justify-center">
              <div className="flex items-center gap-3">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#c66d59] border-t-transparent" />
                <span className="font-medium text-[#c66d59]">Vérification du mois…</span>
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 text-sm text-gray-700">
                La clôture enregistre une <strong>archive définitive</strong> du mois. Elle reste
                consultable dans l'historique des clôtures.
              </div>

              {/* Conditions de clôture */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Vérification des conditions</h4>

                {dejaCloture ? (
                  <div className="flex items-start gap-3 rounded-lg border-2 border-gray-300 bg-gray-50 p-4">
                    <Lock size={20} className="mt-0.5 flex-shrink-0 text-gray-600" />
                    <div>
                      <p className="font-medium text-gray-900">Mois déjà clôturé</p>
                      <p className="mt-1 text-sm text-gray-700">
                        {moisLabel} {annee} a été clôturé le{' '}
                        {verification?.date_cloture
                          ? new Date(verification.date_cloture).toLocaleString('fr-FR')
                          : '—'}.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`flex items-start gap-3 rounded-lg border-2 p-4 ${sansAttente ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                      {sansAttente
                        ? <CheckCircle size={20} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                        : <XCircle size={20} className="mt-0.5 flex-shrink-0 text-red-600" />}
                      <div>
                        <p className={`font-medium ${sansAttente ? 'text-emerald-900' : 'text-red-900'}`}>
                          Caisses en attente de validation
                        </p>
                        <p className={`mt-1 text-sm ${sansAttente ? 'text-emerald-800' : 'text-red-800'}`}>
                          {sansAttente
                            ? 'Toutes les caisses du mois ont été validées'
                            : `${stats.caisses_en_attente} caisse(s) en attente sur ${moisLabel}`}
                        </p>
                      </div>
                    </div>

                    <div className={`flex items-start gap-3 rounded-lg border-2 p-4 ${sansObservation ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
                      {sansObservation
                        ? <CheckCircle size={20} className="mt-0.5 flex-shrink-0 text-emerald-600" />
                        : <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />}
                      <div>
                        <p className={`font-medium ${sansObservation ? 'text-emerald-900' : 'text-amber-900'}`}>
                          Observations en cours
                        </p>
                        <p className={`mt-1 text-sm ${sansObservation ? 'text-emerald-800' : 'text-amber-800'}`}>
                          {sansObservation
                            ? 'Aucune observation non résolue sur ce mois'
                            : `${stats.caisses_avec_observation} caisse(s) avec observations non résolues`}
                        </p>
                      </div>
                    </div>

                    {verification?.raison === 'aucune_caisse' && (
                      <div className="flex items-start gap-3 rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
                        <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />
                        <div>
                          <p className="font-medium text-amber-900">Aucune caisse sur ce mois</p>
                          <p className="mt-1 text-sm text-amber-800">
                            Aucune remise n'a été enregistrée sur {moisLabel} {annee} : il n'y a rien à archiver.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Récapitulatif du mois */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Récapitulatif de {moisLabel} {annee}</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4">
                    <p className="mb-1 text-sm text-emerald-700">Total encaissements</p>
                    <p className="text-2xl font-bold tabular-nums text-emerald-600">
                      {formatMontant(totaux.total_encaissements || 0)}
                    </p>
                  </div>
                  <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4">
                    <p className="mb-1 text-sm text-red-700">Total dépenses</p>
                    <p className="text-2xl font-bold tabular-nums text-red-600">
                      {formatMontant(totaux.total_depenses || 0)}
                    </p>
                  </div>
                </div>

                <div className="rounded-lg bg-[#246990] p-4">
                  <p className="mb-1 text-sm text-white/80">Solde archivé du mois</p>
                  <p className="text-3xl font-bold tabular-nums text-white">{formatMontant(totaux.solde || 0)}</p>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-2">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                      <Receipt size={20} className="text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Transactions</p>
                      <p className="text-lg font-bold text-gray-900">{totaux.nb_transactions || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                      <Users size={20} className="text-gray-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Gestionnaires</p>
                      <p className="text-lg font-bold text-gray-900">{totaux.nb_gestionnaires || 0}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
                      <CheckCircle size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Caisses validées</p>
                      <p className="text-lg font-bold text-emerald-600">{totaux.nb_caisses_validees || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Détail par gestionnaire */}
              {detailsGestionnaires.length > 0 && (
                <div className="rounded-lg border-2 border-[#246990]/20 bg-[#eef7fb]/40">
                  <div className="border-b border-[#246990]/20 bg-[#eef7fb] p-4">
                    <h4 className="flex items-center gap-2 text-base font-semibold text-[#246990]">
                      <Users size={18} />
                      Détail par gestionnaire
                    </h4>
                  </div>
                  <div className="overflow-x-auto p-4">
                    <table className="w-full">
                      <thead className="border-b border-[#246990]/20 bg-[#eef7fb]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-[#246990]">Gestionnaire</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold uppercase text-[#246990]">Caisses</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-[#246990]">Encaissements</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-[#246990]">Dépenses</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-[#246990]">Solde</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#246990]/10">
                        {detailsGestionnaires.map((g) => (
                          <tr key={g.gestionnaire_id} className="transition-colors hover:bg-[#eef7fb]">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {g.gestionnaire_prenom} {g.gestionnaire_nom}
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-700">{g.nb_caisses}</td>
                            <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-emerald-600">{formatMontant(g.total_encaissements)}</td>
                            <td className="px-4 py-3 text-right text-sm font-semibold tabular-nums text-red-600">{formatMontant(g.total_depenses)}</td>
                            <td className="px-4 py-3 text-right text-sm font-bold tabular-nums text-[#246990]">{formatMontant(g.solde)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Note de clôture */}
              {peutCloturer && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Note de clôture (optionnelle)
                  </label>
                  <textarea
                    value={noteCloture}
                    onChange={(e) => setNoteCloture(e.target.value)}
                    placeholder="Ajoutez un commentaire qui restera attaché à l'archive du mois…"
                    rows={3}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#c66d59]"
                  />
                </div>
              )}

              {/* Verdict */}
              {peutCloturer ? (
                <>
                  <div className="rounded border-l-4 border-emerald-500 bg-emerald-50 p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle size={20} className="mt-0.5 text-emerald-600" />
                      <div>
                        <h5 className="mb-1 font-semibold text-emerald-900">Prêt pour la clôture</h5>
                        <p className="text-sm text-emerald-800">{verification?.message}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded border-l-4 border-red-500 bg-red-50 p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle size={20} className="mt-0.5 text-red-600" />
                      <div>
                        <h5 className="mb-1 font-semibold text-red-900">Action irréversible</h5>
                        <p className="text-sm text-red-800">
                          Une fois {moisLabel} clôturé, l'archive comptable est définitive et ne pourra plus être modifiée.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="rounded border-l-4 border-red-500 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <XCircle size={20} className="mt-0.5 text-red-600" />
                    <div>
                      <h5 className="mb-1 font-semibold text-red-900">Clôture impossible</h5>
                      <p className="text-sm text-red-800">
                        {verification?.message || 'Les conditions de clôture ne sont pas remplies.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <Button
            variant="outline"
            className={BOUTON_PIED_CLASSES}
            onClick={onClose}
            disabled={isLoading}
          >
            {peutCloturer ? 'Annuler' : 'Fermer'}
          </Button>
          {peutCloturer && (
            <Button
              className={`${BOUTON_PIED_CLASSES} bg-[#c66d59] text-white hover:bg-[#ae7467]`}
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Clôture…
                </>
              ) : (
                <>
                  <Lock size={16} />
                  Confirmer la clôture
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClotureMoisModal;
